// ── Gathering node configuration (Round 13 Phase B) ──
//
// Per-node-type config: gather duration, material output, cooldown, and
// yield probability. Yield rates derived from material-economy-model.md §1
// baseGatherRate (units/hour) converted to per-interaction probability.
import { ItemType } from '../types/loot.js';
import { GatherNodeType } from '../types/gathering.js';
/**
 * Per-interaction yield rates derived from economy model:
 * baseGatherRate (units/hr) / (3600s/hr / gatherDurationS) = units per action.
 *
 * Wood:  200/hr, 5s gather -> 200/(3600/5) = 0.278 -> 1 per action (rounded up for feel)
 * Stone:  60/hr, 5s gather -> 60/720 = 0.083 -> 1 per action (guaranteed 1, cooldown gates rate)
 * Thatch: 80/hr, 4s gather -> 80/900 = 0.089 -> 1 per action
 * Water:  10/hr, 3s gather -> 10/1200 = 0.008 -> 1 per action (long cooldown)
 * Ink:    15/hr, 6s gather -> 15/600 = 0.025 -> 1 per action (long cooldown)
 * Cloth:  20/hr, 8s gather -> 20/450 = 0.044 -> 1 per action (long cooldown)
 * Wax:    15/hr, 6s gather -> 15/600 = 0.025 -> 1 per action (long cooldown)
 * Ash:     8/hr, 3s gather -> 8/1200 = 0.007 -> 1 per action (longest cooldown)
 * Ember:  10/hr, 4s gather -> 10/900 = 0.011 -> 1 per action (long cooldown)
 *
 * All yield 1 unit per action; the economy throttle comes from cooldown duration.
 * Primary materials: 30s cooldown. Secondary/regional: 45-60s cooldown.
 */
export const GATHER_NODE_CONFIGS = {
    [GatherNodeType.Tree]: {
        nodeType: GatherNodeType.Tree,
        durationMs: 5_000,
        outputItem: ItemType.MaterialWood,
        cooldownMs: 30_000,
        yieldQuantity: 1,
    },
    [GatherNodeType.Rock]: {
        nodeType: GatherNodeType.Rock,
        durationMs: 5_000,
        outputItem: ItemType.MaterialStone,
        cooldownMs: 30_000,
        yieldQuantity: 1,
    },
    [GatherNodeType.Grass]: {
        nodeType: GatherNodeType.Grass,
        durationMs: 4_000,
        outputItem: ItemType.MaterialThatch,
        cooldownMs: 30_000,
        yieldQuantity: 1,
    },
    [GatherNodeType.WaterSource]: {
        nodeType: GatherNodeType.WaterSource,
        durationMs: 3_000,
        outputItem: ItemType.MaterialThawedWater,
        cooldownMs: 60_000,
        yieldQuantity: 1,
    },
    [GatherNodeType.InkStone]: {
        nodeType: GatherNodeType.InkStone,
        durationMs: 6_000,
        outputItem: ItemType.MaterialInk,
        cooldownMs: 45_000,
        yieldQuantity: 1,
    },
    [GatherNodeType.HomeBench]: {
        nodeType: GatherNodeType.HomeBench,
        durationMs: 8_000,
        outputItem: ItemType.MaterialCloth,
        cooldownMs: 45_000,
        yieldQuantity: 1,
    },
    [GatherNodeType.Beehive]: {
        nodeType: GatherNodeType.Beehive,
        durationMs: 6_000,
        outputItem: ItemType.MaterialWax,
        cooldownMs: 45_000,
        yieldQuantity: 1,
    },
    [GatherNodeType.FireResidue]: {
        nodeType: GatherNodeType.FireResidue,
        durationMs: 3_000,
        outputItem: ItemType.MaterialAsh,
        cooldownMs: 60_000,
        yieldQuantity: 1,
    },
    [GatherNodeType.EmberVent]: {
        nodeType: GatherNodeType.EmberVent,
        durationMs: 4_000,
        outputItem: ItemType.MaterialEmber,
        cooldownMs: 60_000,
        yieldQuantity: 1,
    },
};
//# sourceMappingURL=gatheringNodes.js.map